import type { BinaryImage } from './threshold.js';

/** The search walks whole multiples of this, in degrees. */
const SKEW_STEP_DEGREES = 0.1;

/**
 * The angle the sheet is turned by, in degrees, positive anticlockwise.
 *
 * Found by projection profiling: shear the ink by a candidate angle, sum each
 * row, and take the angle whose row sums vary most. A drawing frame and its
 * horizontal rules put almost all their ink on a few rows when the sheet is
 * square, and smear it across many when it is not, so the variance peaks at the
 * true angle. This needs no line detector, which matters because deskewing is a
 * *precondition* for the detector rather than a consumer of it.
 *
 * `maxDegrees` is a deliberate limit. A page turned 90 degrees is an orientation
 * problem and correcting it as a skew would destroy the drawing, so anything
 * beyond the window is reported as no skew and left for a human.
 *
 * The candidate angles are whole multiples of the step rather than a running
 * sum, so the answer carries no accumulated addition error and two runs return
 * the identical double.
 */
export function estimateSkewDegrees(image: BinaryImage, maxDegrees = 3): number {
  // The ink coordinates are gathered once and reused for every candidate angle.
  // The profile only ever looks at ink, and there are 61 candidates: walking all
  // 34.8 million pixels of a 600 DPI sheet once per angle costs 2.1 billion
  // reads to examine the same 2% of them each time.
  const ink = inkCoordinates(image);
  if (ink.length === 0) return 0;

  const steps = Math.floor(maxDegrees / SKEW_STEP_DEGREES);
  let plateauStart = 0;
  let plateauEnd = 0;
  let bestScore = -1;

  for (let i = -steps; i <= steps; i += 1) {
    const score = projectionVariance(ink, image.height, image.width, i * SKEW_STEP_DEGREES);
    if (score > bestScore) {
      bestScore = score;
      plateauStart = i;
      plateauEnd = i;
    } else if (score === bestScore) {
      plateauEnd = i;
    }
  }

  // The midpoint of the maximising plateau, for the same reason `otsuThreshold`
  // takes one: a square sheet scores *identically* at -0.1, 0.0 and +0.1, since
  // a shear of 0.0017 moves no pixel of a 400-wide sheet into another row. Taking
  // the first of the tie reported -0.1 on a sheet with no skew at all, and a
  // deskew step that rotates a good scan by a tenth of a degree for no reason is
  // a resample nobody asked for.
  return ((plateauStart + plateauEnd) / 2) * SKEW_STEP_DEGREES;
}

/** Ink pixel positions, x and y interleaved. */
function inkCoordinates(image: BinaryImage): Int32Array {
  const { width, height, ink } = image;
  let count = 0;
  for (let i = 0; i < ink.length; i += 1) if (ink[i] !== 0) count += 1;

  const out = new Int32Array(count * 2);
  let at = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (ink[y * width + x] === 0) continue;
      out[at] = x;
      out[at + 1] = y;
      at += 2;
    }
  }
  return out;
}

function projectionVariance(
  ink: Int32Array,
  height: number,
  width: number,
  degrees: number,
): number {
  const slope = Math.tan((degrees * Math.PI) / 180);
  const rows = new Float64Array(height);
  const centre = width / 2;

  for (let i = 0; i < ink.length; i += 2) {
    const shifted = Math.round(ink[i + 1]! - slope * (ink[i]! - centre));
    if (shifted >= 0 && shifted < height) rows[shifted] = rows[shifted]! + 1;
  }

  let mean = 0;
  for (const value of rows) mean += value;
  mean /= height;
  let variance = 0;
  for (const value of rows) variance += (value - mean) ** 2;
  return variance;
}

/**
 * Rotates about the image centre with nearest-neighbour sampling.
 *
 * Nearest neighbour rather than bilinear on purpose: the input is already
 * binary, so interpolation would invent grey values that then need thresholding
 * a second time. One resample, no second decision.
 */
export function rotateBinary(image: BinaryImage, degrees: number): BinaryImage {
  const { width, height, ink } = image;
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const cx = (width - 1) / 2;
  const cy = (height - 1) / 2;
  const out = new Uint8Array(ink.length);

  for (let y = 0; y < height; y += 1) {
    const dy = y - cy;
    for (let x = 0; x < width; x += 1) {
      const dx = x - cx;
      // Sample the source at the inverse rotation of this destination pixel.
      const sx = Math.round(cx + dx * cos + dy * sin);
      const sy = Math.round(cy - dx * sin + dy * cos);
      if (sx < 0 || sx >= width || sy < 0 || sy >= height) continue;
      out[y * width + x] = ink[sy * width + sx]!;
    }
  }
  return { width, height, ink: out };
}
